import {
    clampColors,
    clampInclusions,
    clampSondagePhotos,
    clampStrata,
    diaryEntity,
    findingPlanumEntity,
    findingProfileEntity,
    formatInclusions,
    formatQuotaRange,
    formatStrata,
    parseCrewArray,
    parsePhaseArray,
    parseQuotaArray,
    projectEntity
} from '@ast-data/shared';
import { compareText } from '../../utils/compare';

// ---------------------------------------------------------------------------
/**
 * Embedded lists in a version history.
 *
 * A handful of fields are lists of OBJECTS on the doc — the Tagebuch's visitors,
 * a Planum's Farben, Beimengungen and Schichten. The dialog diffs a field as
 * text, and text is what `String(value)` makes of an object: a column of
 * "[object Object], [object Object]" that names neither what was there nor what
 * changed.
 *
 * So each of them gets the spelling its own screen uses, ONE ENTRY PER LINE: the
 * diff is line-oriented, so an added visitor reads as an added line instead of
 * smearing through a single joined one.
 */
// ---------------------------------------------------------------------------

/** A snapshot value read as a list, however the push happened to carry it. */
function asList(value: unknown): unknown[] {
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        try {
            const parsed: unknown = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
}

/** One entry's field as trimmed text, whatever the snapshot stored. */
function entryText(entry: unknown, key: string): string {
    const record = (entry ?? {}) as Record<string, unknown>;
    const value = record[key];
    return value === undefined || value === null ? '' : String(value).trim();
}

/**
 * "Name (Funktion): Notiz", one visitor per line — the same grammar the ExcaBook
 * column and the field sheets write (see the export's `visitorsText`).
 */
function formatVisitors(rawValue: unknown): string {
    return asList(rawValue)
        .map((entry) => {
            const name = entryText(entry, 'name');
            const position = entryText(entry, 'position');
            const note = entryText(entry, 'note');
            const who = position ? `${name} (${position})` : name;
            return note ? `${who}: ${note}` : who;
        })
        .filter((line) => line !== '' && line !== '()')
        .join('\n');
}

/** "dunkelbraun 10YR 3/2", one colour per line — as the Befunde grid spells it. */
function formatColors(rawValue: unknown): string {
    return clampColors(rawValue)
        .map((entry) =>
            [entry.color, entry.munsellCode].filter(Boolean).join(' ')
        )
        .filter(Boolean)
        .join('\n');
}

/** "Holzkohle (<1%)", one Beimengung per line. */
function formatInclusionLines(rawValue: unknown): string {
    return clampInclusions(rawValue)
        .map((entry) => formatInclusions([entry]))
        .filter(Boolean)
        .join('\n');
}

/** "Schicht A (L 120 cm, B 80 cm)", one Schicht per line. */
function formatStratumLines(rawValue: unknown): string {
    return clampStrata(rawValue)
        .map((entry) => formatStrata([entry]))
        .filter(Boolean)
        .join('\n');
}

/** "Sondage 12 / Teilfoto 4", one pair per line — as the editor shows them. */
function formatSondagePhotos(rawValue: unknown): string {
    return clampSondagePhotos(rawValue)
        .map((entry) =>
            [
                entry.sondage && `Sondage ${entry.sondage}`,
                entry.partialPhoto && `Teilfoto ${entry.partialPhoto}`
            ]
                .filter(Boolean)
                .join(' / ')
        )
        .filter(Boolean)
        .join('\n');
}

/**
 * "1000–1999 · Schnitt 1", one Kontingent per line — the spelling the Kontingente
 * screen uses for a range.
 *
 * Exported because the Kontingente page's own Verlauf renders the same field:
 * one spelling, so the change log and the screen the change was made on do not
 * describe it differently.
 */
export function formatQuotas(rawValue: unknown): string {
    return parseQuotaArray(rawValue)
        .map((quota) =>
            [formatQuotaRange(quota), quota.label].filter(Boolean).join(' · ')
        )
        .join('\n');
}

/**
 * "Anna Beispiel · Grabungsleitung, Zeichnerin", one crew member per line.
 *
 * Sorted by name, unlike the Phasen and Kontingente beside it. Those are lists
 * somebody writes in an order they chose, so their order is part of the record.
 * The Grabungsmannschaft assembles itself from the person fields of every
 * record in the project, so its order says nothing and shifts on its own — and
 * an unsorted diff would then report a reshuffle as if the crew had changed.
 */
function formatCrew(rawValue: unknown): string {
    return parseCrewArray(rawValue)
        .map((member) =>
            [member.name, member.roles.join(', ')].filter(Boolean).join(' · ')
        )
        .filter(Boolean)
        .sort(compareText)
        .join('\n');
}

/** "2 · Bauabschnitt Nord · BA Nord · 2026-04-01", one Phase per line. */
export function formatPhases(rawValue: unknown): string {
    return parsePhaseArray(rawValue)
        .map((phase) =>
            [phase.number, phase.name, phase.shortTitle, phase.startDate]
                .filter(Boolean)
                .join(' · ')
        )
        .join('\n');
}

const SOIL_FORMATS: Record<string, (rawValue: unknown) => string> = {
    colors: formatColors,
    inclusions: formatInclusionLines,
    strata: formatStratumLines
};

/** Per entity type, the snapshot fields that need a spelling of their own. */
const FORMATS: Record<string, Record<string, (rawValue: unknown) => string>> = {
    [diaryEntity.name]: {
        visitors: formatVisitors,
        sondagePhotos: formatSondagePhotos
    },
    [findingPlanumEntity.name]: SOIL_FORMATS,
    [findingProfileEntity.name]: SOIL_FORMATS,
    /**
     * All four live on the project document. Phasen and Kontingente have a
     * Verlauf of their own on their own screens, which is why the Metadaten
     * dialog leaves them out, and the Grabungsmannschaft has none at all. The
     * change log has no such split — it is the only place a project version is
     * read whole, so it has to render every one of them itself.
     */
    [projectEntity.name]: {
        crew: formatCrew,
        phases: formatPhases,
        findingQuotas: formatQuotas,
        profileQuotas: formatQuotas,
        findSlipQuotas: formatQuotas
    }
};

/**
 * How to render one snapshot field as diffable text, or undefined for the fields
 * that read fine as they are. Feed it to `HistoryField.format`.
 */
export function historyValueFormat(
    entityType: string,
    key: string
): ((rawValue: unknown) => string) | undefined {
    return FORMATS[entityType]?.[key];
}
